import { Edge, Node, XYPosition } from '@xyflow/react';

const SQUARED = (value: number) => value * value;

const squaredDistance = (a: XYPosition, b: XYPosition) =>
  SQUARED(a.x - b.x) + SQUARED(a.y - b.y);

/** Squared distance from point to segment AB — avoids sqrt in hot paths. */
const distanceToSegmentSquared = (
  point: XYPosition,
  start: XYPosition,
  end: XYPosition
): number => {
  const segmentLengthSquared = squaredDistance(start, end);
  if (segmentLengthSquared === 0) {
    return squaredDistance(point, start);
  }

  const projection =
    ((point.x - start.x) * (end.x - start.x) +
      (point.y - start.y) * (end.y - start.y)) /
    segmentLengthSquared;
  const clampedProjection = Math.max(0, Math.min(1, projection));
  const projectedPoint = {
    x: start.x + clampedProjection * (end.x - start.x),
    y: start.y + clampedProjection * (end.y - start.y),
  };

  return squaredDistance(point, projectedPoint);
};

export type EdgeHitCandidate = {
  edge: Edge;
  distance: number;
};

type NodeCenterMap = Map<string, XYPosition>;
type NodeTypeMap = Map<string, string | undefined>;

const DISTANCE_EPS = 1e-4;

/** Upper bound for squared distance when linear distance is within min + eps of sqrt(minSq). */
const tieMaxSquared = (minSq: number) =>
  minSq + 2 * Math.sqrt(minSq) * DISTANCE_EPS + SQUARED(DISTANCE_EPS);

const buildNodeLookups = (
  nodes: Node[]
): { centerMap: NodeCenterMap; typeById: NodeTypeMap } => {
  const centerMap: NodeCenterMap = new Map();
  const typeById: NodeTypeMap = new Map();

  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    const width = node.measured?.width ?? 300;
    const height = node.measured?.height ?? 120;
    centerMap.set(node.id, {
      x: node.position.x + width / 2,
      y: node.position.y + height / 2,
    });
    typeById.set(node.id, (node.data as { type?: string } | undefined)?.type);
  }

  return { centerMap, typeById };
};

/** True/false placeholder edges share the same center-to-center segment; use layout (true left, false right). */
const tieBreakOverlappingBranchEdges = (
  candidates: EdgeHitCandidate[],
  point: XYPosition,
  centerMap: NodeCenterMap,
  typeById: NodeTypeMap
): EdgeHitCandidate => {
  if (candidates.length === 1) return candidates[0];

  for (let i = 0; i < candidates.length; i++) {
    for (let j = i + 1; j < candidates.length; j++) {
      const a = candidates[i].edge;
      const b = candidates[j].edge;
      if (a.source !== b.source || a.target !== b.target) continue;
      if (typeById.get(a.source) !== 'true-false') continue;
      const ha = a.sourceHandle;
      const hb = b.sourceHandle;
      if (
        (ha === 'true' && hb === 'false') ||
        (ha === 'false' && hb === 'true')
      ) {
        const center = centerMap.get(a.source);
        if (!center) return candidates[0];
        const preferTrue = point.x < center.x;
        const wantHandle = preferTrue ? 'true' : 'false';
        const picked = candidates.find(
          (c) => c.edge.sourceHandle === wantHandle
        );
        if (picked) return picked;
      }
    }
  }

  return candidates[0];
};

/**
 * Finds the workflow edge whose center-to-center segment is nearest to `point`.
 * Uses squared distances internally (no per-edge sqrt). Optional `excludeNodeIds`
 * skips edges incident to those nodes (e.g. dragging node and its condition block).
 */
export const findNearestWorkflowEdge = (
  point: XYPosition,
  nodes: Node[],
  edges: Edge[],
  maxDistance = 72,
  excludeNodeIds?: Set<string>
): EdgeHitCandidate | null => {
  if (!edges.length) return null;

  const { centerMap, typeById } = buildNodeLookups(nodes);
  const maxDistSq = SQUARED(maxDistance);

  const distSqIfEligible = (edge: Edge): number | null => {
    if (
      excludeNodeIds &&
      (excludeNodeIds.has(edge.source) || excludeNodeIds.has(edge.target))
    ) {
      return null;
    }
    const start = centerMap.get(edge.source);
    const end = centerMap.get(edge.target);
    if (!start || !end) return null;
    const distSq = distanceToSegmentSquared(point, start, end);
    return distSq > maxDistSq ? null : distSq;
  };

  let minDistSq = Infinity;
  for (let e = 0; e < edges.length; e++) {
    const d = distSqIfEligible(edges[e]);
    if (d !== null && d < minDistSq) minDistSq = d;
  }

  if (minDistSq === Infinity) return null;

  const tieBoundSq = tieMaxSquared(minDistSq);
  const atMin: EdgeHitCandidate[] = [];

  for (let e = 0; e < edges.length; e++) {
    const edge = edges[e];
    const distSq = distSqIfEligible(edge);
    if (distSq === null || distSq > tieBoundSq) continue;
    atMin.push({ edge, distance: Math.sqrt(distSq) });
  }

  if (atMin.length === 0) return null;
  if (atMin.length === 1) return atMin[0];

  return tieBreakOverlappingBranchEdges(atMin, point, centerMap, typeById);
};
